import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon
} from '@heroicons/react/16/solid';
import {
  DiffEditor,
  DiffEditorProps,
  Editor,
  EditorProps,
  Monaco
} from '@monaco-editor/react';
import {
  Box,
  Button,
  classSet,
  Modal
} from '@platform/ui-library';
import { JSHINT as jshint } from 'jshint';
import { default as MonacoEditor } from 'monaco-editor';
import {
  FC,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useUserSettings } from '@app/hooks';
import { CodeEditorTheme } from '@app/types';
import { modifierKey } from '@app/utilities/functions';
import { ClickOutside } from '../ClickOutside';
import { initMonacoEditor } from './initMonacoEditor';
import styles from './CodeEditor.module.scss';

initMonacoEditor();

type IMarkerData = MonacoEditor.editor.IMarkerData;

type IStandaloneCodeEditor = MonacoEditor.editor.IStandaloneCodeEditor;

/** Broom icon for the beautify button. */
const BeautifyIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Broom - tilted to the right */}
    <g transform="rotate(25, 12, 12)">
      {/* Handle */}
      <rect x="10.5" y="1" width="3" height="11" rx="1.5" />
      {/* Binding/ferrule */}
      <rect x="8" y="11" width="8" height="3" rx="0.5" />
      {/* Bristles */}
      <path d="M8 14 L6 23 L18 23 L16 14 Z" />
      <line x1="8.5" y1="14" x2="7" y2="23" stroke="currentColor" strokeWidth="1" opacity="0.3" />
      <line x1="10.5" y1="14" x2="9.5" y2="23" stroke="currentColor" strokeWidth="1" opacity="0.3" />
      <line x1="12" y1="14" x2="12" y2="23" stroke="currentColor" strokeWidth="1" opacity="0.3" />
      <line x1="13.5" y1="14" x2="14.5" y2="23" stroke="currentColor" strokeWidth="1" opacity="0.3" />
      <line x1="15.5" y1="14" x2="17" y2="23" stroke="currentColor" strokeWidth="1" opacity="0.3" />
    </g>
  </svg>
);

const codeEditorThemeLabels: Record<CodeEditorTheme, string> = {
  [CodeEditorTheme.githubLight]: 'GitHub Light',
  [CodeEditorTheme.solarizedLight]: 'Solarized Light',
  [CodeEditorTheme.dracula]: 'Dracula',
  [CodeEditorTheme.oceanicNext]: 'Oceanic Next'
};

/** Valid programming languages @see CodeEditor supports. */
export enum CodeEditorLanguage {
  javascript = 'javascript',
  json = 'json',
  xml = 'xml'
}

/** Handle exposed by @see CodeEditor via the @see editorRef prop. */
export type CodeEditorHandle = {
  /** Formats the editor content using Monaco's built-in formatter. */
  beautify: () => Promise<string>;
  /** Exits fullscreen mode if the editor is currently maximized. */
  closeMaximized: () => void;
  /** Current editor buffer text. */
  getValue: () => string;
};

export type CodeEditorProps = {
  /** Additional actions to render in the toolbar (shown in both normal and expanded views). */
  additionalActions?: React.ReactNode;
  /** Ref callback that receives a @see CodeEditorHandle for imperative access. */
  editorRef?: (handle: CodeEditorHandle | null) => void;
  /** Function signature header displayed above the editor (e.g., "function (arg1, arg2) {"). */
  header?: React.ReactNode;
  /** Programming language to show syntax highlighting for @default {CodeEditorLanguage.javascript}. */
  language?: CodeEditorLanguage;
  /** Callback triggered when Cmd+H / Ctrl+H is pressed to open AI assist. */
  onAiAssist?: () => void;
  /** Callback triggered when Cmd+J / Ctrl+J is pressed to explain code. */
  onExplainCode?: () => void;
  /** Callback triggered when Cmd+R / Ctrl+R is pressed to run the script. */
  onRunScript?: () => void;
  /** Callback triggered when Cmd+S / Ctrl+S is pressed to save. */
  onSave?: () => void;
  /** Callback triggered when Cmd+I / Ctrl+I is pressed to toggle inputs view. */
  onToggleInputs?: () => void;
  /** Switches the editor into diff view and compares the current @see value with this property. */
  originalValue?: string;
  /** Code as text. */
  value: string | null | undefined;
} & (
  {
    /** The code has changed. */
    onChange?: never;
    /** Disallow editing the @see value @default false. */
    readOnly: true;
  } | {
    /** The code has changed. */
    onChange: (value: string) => void;
    /** Disallow editing the @see value @default false. */
    readOnly?: boolean;
  }
);

/** Displays an editable text area with code syntax highlighting. */
export const CodeEditor: FC<CodeEditorProps> = ({
  additionalActions,
  editorRef,
  header,
  language = CodeEditorLanguage.javascript,
  onAiAssist,
  onExplainCode,
  onRunScript,
  onSave,
  onToggleInputs,
  originalValue,
  onChange,
  readOnly = false,
  value
}) => {
  value ??= '';
  const editorTheme = useUserSettings((s) => s.codeEditorTheme);
  const setEditorTheme = useUserSettings((s) => s.setCodeEditorTheme);
  const [editor, setEditor] = useState<IStandaloneCodeEditor | null>(null);
  const [monaco, setMonaco] = useState<Monaco>();
  const [maximized, setMaximized] = useState<boolean>(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const initialValueRef = useRef(value);
  const isDiffMode = originalValue != null;

  const handleBeautify = () => {
    editor?.getAction('editor.action.formatDocument')?.run();
  };

  // Expose imperative handle to parent via editorRef callback
  useEffect(() => {
    if (!editorRef) {
      return;
    }

    if (!editor) {
      editorRef(null);
      return;
    }

    const handle: CodeEditorHandle = {
      beautify: async () => {
        if (!editor) {
          return '';
        }
        await editor.getAction('editor.action.formatDocument')?.run();
        return editor.getValue();
      },
      closeMaximized: () => setMaximized(false),
      getValue: () => editor.getValue()
    };

    editorRef(handle);
    return () => editorRef(null);
  }, [editorRef, editor]);

  // Apply theme when it changes
  useEffect(() => {
    if (!monaco) {
      return;
    }
    monaco.editor.setTheme(editorTheme);
  }, [monaco, editorTheme]);

  const applyJshint = useCallback((source: string) => {
    const model = editor?.getModel();
    if (
      !model
      || !monaco
      || language !== CodeEditorLanguage.javascript
    ) {
      return;
    } else if (!source) {
      monaco.editor.setModelMarkers(model, 'errors', []);
      return;
    }

    jshint(source, { esversion: 11 });

    const errors = jshint.data()?.errors?.map(({
      a: matchA,
      character: column,
      line,
      reason,
      code
    }): IMarkerData => {
      return {
        endColumn: column + ((typeof matchA === 'string') ? matchA.length : 0),
        endLineNumber: line,
        message: `JSH ${code}: ${reason}`,
        severity: (code.startsWith('W')) ? monaco.MarkerSeverity.Warning : monaco.MarkerSeverity.Error,
        startColumn: column,
        startLineNumber: line
      };
    }) ?? [];

    monaco.editor.setModelMarkers(model, 'errors', errors);
  }, [editor, language, monaco]);

  // Push external value updates (AI accept, load) without resetting on parent echo while typing.
  useEffect(() => {
    if (!editor || isDiffMode) {
      return;
    }

    const current = editor.getValue();
    if (value === current) {
      return;
    }

    editor.setValue(value);
    applyJshint(value);
  }, [applyJshint, editor, isDiffMode, value]);

  // @see DiffEditor does not have an onChange property, so we handle it ourselves.
  useEffect(() => {
    const model = editor?.getModel();
    if (!model) {
      return;
    }

    const listener = model.onDidChangeContent(() => {
      const nextValue = model.getValue();
      applyJshint(nextValue);
      onChange?.(nextValue);
    });

    applyJshint(model.getValue());

    return () => listener.dispose();
  }, [applyJshint, editor, onChange]);

  // Register keyboard shortcuts when editor mounts
  useEffect(() => {
    if (!editor || !monaco) {
      return;
    }

    const disposables: MonacoEditor.IDisposable[] = [];

    // Cmd+B / Ctrl+B: Beautify (only if not readOnly)
    if (!readOnly) {
      disposables.push(
        editor.addAction({
          id: 'beautify-code',
          label: 'Beautify Code',
          keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyB],
          run: () => {
            editor.getAction('editor.action.formatDocument')?.run();
          }
        })
      );
    }

    // Cmd+G / Ctrl+G: Toggle expand/maximize
    disposables.push(
      editor.addAction({
        id: 'toggle-expand',
        label: 'Toggle Expand',
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyG],
        run: () => {
          setMaximized((prev) => !prev);
        }
      })
    );

    // Cmd+H / Ctrl+H: Open AI Assist (only if callback provided and not readOnly)
    if (onAiAssist && !readOnly) {
      disposables.push(
        editor.addAction({
          id: 'ai-assist',
          label: 'AI Assist',
          keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyH],
          run: () => {
            setMaximized(false);
            onAiAssist();
          }
        })
      );
    }

    // Cmd+J / Ctrl+J: Explain Code (only if callback provided)
    if (onExplainCode) {
      disposables.push(
        editor.addAction({
          id: 'explain-code',
          label: 'Explain Code',
          keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyJ],
          run: () => {
            setMaximized(false);
            onExplainCode();
          }
        })
      );
    }

    // Cmd+I / Ctrl+I: Toggle inputs view (only if callback provided)
    if (onToggleInputs) {
      disposables.push(
        editor.addAction({
          id: 'toggle-inputs',
          label: 'Toggle Inputs View',
          keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyI],
          run: () => {
            onToggleInputs();
          }
        })
      );
    }

    // Cmd+R / Ctrl+R: Run script (only if callback provided)
    if (onRunScript) {
      disposables.push(
        editor.addAction({
          id: 'run-script',
          label: 'Run Script',
          keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyR],
          run: () => {
            setMaximized(false);
            onRunScript();
          }
        })
      );
    }

    // Cmd+S / Ctrl+S: Save (only if callback provided and not readOnly)
    if (onSave && !readOnly) {
      disposables.push(
        editor.addAction({
          id: 'save-code',
          label: 'Save',
          keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
          run: () => {
            onSave();
          }
        })
      );
    }

    return () => {
      disposables.forEach((d) => d.dispose());
    };
  }, [editor, monaco, onAiAssist, onExplainCode, onRunScript, onSave, onToggleInputs, readOnly]);

  // Focus editor on mount and when maximize state changes (preserve cursor position).
  useEffect(() => {
    if (!editor) {
      return;
    }

    const timer = setTimeout(() => {
      editor.focus();
    }, 50);

    return () => clearTimeout(timer);
  }, [editor, maximized]);

  const props = {
    language,
    theme: editorTheme,
    options: {
      minimap: {
        enabled: false
      },
      readOnly,
      padding: {
        top: 16,
        bottom: 16
      },
      wordWrap: 'on'
    }
  } satisfies DiffEditorProps & EditorProps;

  const editorElement = (isDiffMode) ? (
    <DiffEditor
      {...props}
      original={originalValue ?? ''}
      onMount={(diffEditor, monacoInstance) => {
        setEditor(diffEditor.getModifiedEditor());
        setMonaco(monacoInstance);
      }}
      modified={value}
    />
  ) : (
    <Editor
      {...props}
      defaultValue={initialValueRef.current}
      onMount={(editorInstance, monacoInstance) => {
        setEditor(editorInstance);
        setMonaco(monacoInstance);
      }}
    />
  );

  const actions = (
    <div className={styles.actions}>
      {!readOnly && (
        <div className={styles.actionSlot}>
          <Button
            icon={BeautifyIcon}
            invert
            onClick={handleBeautify}
            theme={['outline', 'small', 'square']}
            tooltip={`Beautify code (${modifierKey}B)`}
            type="button"
          />
        </div>
      )}
      <ClickOutside
        className={styles.themeSelector}
        onClickOutside={() => setShowThemeMenu(false)}
      >
        <Button
          icon="settings"
          invert
          onClick={() => setShowThemeMenu(!showThemeMenu)}
          theme={['outline', 'small', 'square']}
          tooltip="Change editor theme"
          type="button"
        />
        {showThemeMenu && (
          <div className={styles.themeMenu}>
            {Object.values(CodeEditorTheme).map((theme) => (
              <button
                key={theme}
                className={classSet([
                  styles.themeOption,
                  theme === editorTheme && styles.active
                ])}
                onClick={() => {
                  setEditorTheme(theme);
                  setShowThemeMenu(false);
                }}
                type="button"
              >
                <span
                  className={styles.themePreview}
                  data-theme={theme}
                />
                {codeEditorThemeLabels[theme]}
              </button>
            ))}
          </div>
        )}
      </ClickOutside>
      <div className={styles.actionSlot}>
        <Button
          icon={() => ((maximized) ? (
            <ArrowsPointingInIcon className={styles.toolbarIcon} />
          ) : (
            <ArrowsPointingOutIcon className={styles.toolbarIcon} />
          ))}
          invert
          onClick={() => setMaximized((isMax) => !isMax)}
          theme={['outline', 'small', 'square']}
          tooltip={(!maximized) ? `Full screen (${modifierKey}G)` : `Exit full screen (${modifierKey}G)`}
          type="button"
        />
      </div>
    </div>
  );

  const editorRow = (
    <div className={styles.editorRow}>
      <div className={styles.content} data-part="script-editor">
        {editorElement}
      </div>
      {(maximized) ? null : actions}
    </div>
  );

  if (maximized) {
    return (
      <Modal
        className={styles.modalWrapper}
        handleClose={() => setMaximized(false)}
        isOpen
      >
        <Box theme='bigPadding'>
          <div className={styles.modalHeader}>
            {additionalActions}
            {actions}
          </div>
          {header && <div className={styles.scriptHeader}>{header}</div>}
          {editorRow}
        </Box>
      </Modal>
    );
  }

  return (
    <div className={styles.container}>
      {additionalActions ? (
        <div className={styles.additionalActions}>
          {additionalActions}
        </div>
      ) : null}
      {header ? (
        <div className={styles.scriptHeader}>{header}</div>
      ) : null}
      {editorRow}
    </div>
  );
};
